import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'
import type { MessageType, PaginatedResponse } from '@/types'

const NOTIFICATIONS_KEY = 'notifications'

export interface MessageTemplate {
  id: string
  name: string
  type: MessageType
  subject?: string
  body: string
  variables: string[]
  // backend field aliases
  title?: string
  eventType?: string
}

export interface BulkSmsPayload {
  recipientType: 'all' | 'branch' | 'group' | 'selected'
  branchId?: string
  groupId?: string
  studentIds?: string[]
  body: string
}

export interface BulkEmailPayload extends BulkSmsPayload {
  subject: string
}

export interface SendResult {
  queued?: number
  failed?: number
  totalSent?: number
  totalFailed?: number
}

// ─── Backend endpoint: POST /notifications/send-bulk
// Backend NotificationChannel enum values: SMS, Email, Push
const CHANNEL_MAP = {
  sms: 'SMS',
  email: 'Email',
  push: 'Push',
} as const

interface BackendSendBulkRequest {
  channel: 'SMS' | 'Email' | 'Push'
  subject: string
  body: string
  recipients: Array<{ userId: string; phone?: string | null; email?: string | null }>
}

const sendBulkNotification = (req: BackendSendBulkRequest) =>
  apiPost<{ totalSent: number; totalFailed: number }>('/notifications/send-bulk', req)

export const useNotificationTemplates = (type?: MessageType) =>
  useQuery({
    queryKey: [NOTIFICATIONS_KEY, 'templates', type],
    queryFn: () =>
      apiGet<PaginatedResponse<MessageTemplate>>('/notifications/templates', {
        params: type ? { type } : undefined,
      }),
  })

export const useSendBulkSms = () =>
  useMutation({
    mutationFn: (payload: BulkSmsPayload) =>
      sendBulkNotification({
        channel: CHANNEL_MAP.sms,
        subject: 'SMS',
        body: payload.body,
        recipients: [],
      }),
  })

export const useSendBulkEmail = () =>
  useMutation({
    mutationFn: (payload: BulkEmailPayload) =>
      sendBulkNotification({
        channel: CHANNEL_MAP.email,
        subject: payload.subject,
        body: payload.body,
        recipients: [],
      }),
  })

export const useSendPushNotification = () =>
  useMutation({
    mutationFn: (payload: BulkSmsPayload & { title: string }) =>
      sendBulkNotification({
        channel: CHANNEL_MAP.push,
        subject: payload.title,
        body: payload.body,
        recipients: [],
      }),
  })

// ─── In-App Notification Types & Hooks ───────────────────────────────────────

export interface InAppNotification {
  id: string
  userId: string
  title: string
  body: string
  link?: string
  type: 'info' | 'success' | 'warning' | 'error'
  isRead: boolean
  createdAt: string
  readAt?: string
}

export interface InAppNotificationsResponse {
  items: InAppNotification[]
  totalCount: number
  pageNumber: number
  totalPages: number
}

export const useMyNotifications = (params?: {
  page?: number
  pageSize?: number
  unreadOnly?: boolean
}) =>
  useQuery({
    queryKey: ['notifications:my', params],
    queryFn: () =>
      apiGet<InAppNotificationsResponse>('/notifications', { params }),
    refetchInterval: 30_000,
  })

export const useUnreadCount = () =>
  useQuery({
    queryKey: ['notifications:unread-count'],
    queryFn: () => apiGet<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 30_000,
  })

export const useMarkAsRead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiPut(`/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications:my'] })
      qc.invalidateQueries({ queryKey: ['notifications:unread-count'] })
    },
  })
}

export const useMarkAllAsRead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPut('/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications:my'] })
      qc.invalidateQueries({ queryKey: ['notifications:unread-count'] })
    },
  })
}
